import React, { useMemo, useState } from "react"
import moment from "moment"
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap"
import { BMD_SECONDS_PER_QUESTION } from "../../../global"
import { BmdPreviewModal } from "../../bmd/components/BmdPreviewModal"
import { EngDiagPreviewModal } from "../../eng-diag/components/EngDiagPreviewModal"
import { useNavigate } from "react-router-dom"
import { ActivityType } from "../../lesson/activity/activity.types"
import reel from "../../../assets/images/services-icon/reel.png"

// The diagnostics card on the lesson plan. It renders both kinds: Math BMD
// (activity_type 13, base and prerequisite tests) and English diagnostics
// (activity_type 15). The two differ only in which session object the backend
// attached, which preview modal opens, and whether a time estimate is shown;
// everything else - status, counts, actions - is the same card.

// 168, not the original 136: widened by fc4ff24 along with PlanLessonCards.tsx
// when the card's action buttons were restyled. The eng-diag branch never
// touched this - it conflicted only because the comment above was added
// directly over it.
const COMPACT_CARD_ACTION_WIDTH = 168

// Shape attached by the tutor-backend SessionActivityModel per-activity-type
// handler for activity_type=13 rows.
export type BmdSessionAttached = {
  pk_diagnostics_session_key?: string
  fk_year_level_id?: number | null
  flow_status?: string | null
  started_on?: string | null
  completed_on?: string | null
  created_on?: string | null
}

// The English counterpart, attached for activity_type=15 rows.
export type EngDiagSessionAttached = {
  pk_session_key?: string
  fk_year_id?: number | null
  flow_status?: string | null
  started_on?: string | null
  completed_on?: string | null
  created_on?: string | null
}

export type BmdActivityLike = {
  pk_lesson_activity_key: string
  fk_tut_key?: string
  fk_student_key?: string
  activity_type: number
  status?: number
  level_name?: string
  roll_date?: string
  tutor_comments?: string | null
  marked_for_homework?: number
  bmd_session?: BmdSessionAttached | null
  eng_diag_session?: EngDiagSessionAttached | null
  topic_count?: number
  question_count?: number
  /** English only: answers the author flagged that are still waiting on a tutor. */
  pending_marking_count?: number
  // True when this activity is a PREREQUISITE TEST rather than a base
  // diagnostic. Both share activity_type 13, so without this the tutor cannot
  // tell them apart on the lesson plan.
  is_prereq_test?: boolean
}

export type BmdCardCheckbox = {
  checkable?: boolean
  checked?: boolean
  onCheckedChanged?: () => unknown
}

export type BmdActivityCardProps = {
  activity: BmdActivityLike
  index: number
  isHomework?: boolean
  isPlanningSession?: boolean
  checkbox?: BmdCardCheckbox
  showContextMenu?: boolean
  onPreview?: () => unknown
  onDelete?: () => unknown
  onRollOver?: () => unknown
  onRolloverToNextLesson?: () => unknown
  onRolloverToHomework?: () => unknown
  onCopyToHomework?: () => unknown
  onCopyToLessonPlan?: () => unknown
  onMoveToHomework?: () => unknown
  onMoveToLessonPlan?: () => unknown
  deleteEnabled?: boolean
  rollOverEnabled?: boolean
}

type ActionTone = "preview" | "delete" | "secondary" | "menu"

function getActionButtonStyle(tone: ActionTone): React.CSSProperties {
  const baseStyle: React.CSSProperties = {
    color: "#1b1b1b",
    border: "none",
    minHeight: "24px",
    padding: "4px 6px",
    lineHeight: 1.05,
    borderRadius: "4px",
  }
  if (tone === "preview") return { ...baseStyle, backgroundColor: "#c9d7f2" }
  if (tone === "delete") return { ...baseStyle, backgroundColor: "#ebc9aa" }
  return { ...baseStyle, backgroundColor: "#d8deec" }
}

const CompactActionButton = ({
  label,
  onClick,
  tone,
  title,
}: {
  label: React.ReactNode
  onClick: () => unknown
  tone: ActionTone
  title?: string
}) => (
  <button
    type="button"
    className="btn btn-sm w-100 fw-700"
    title={title}
    style={{
      ...getActionButtonStyle(tone),
      fontSize: "8px",
      whiteSpace: "nowrap",
      paddingLeft: "2px",
      paddingRight: "2px",
      overflow: "visible",
      lineHeight: 1.1,
    }}
    onMouseDown={(event) => event.stopPropagation()}
    onClick={(event) => {
      event.stopPropagation()
      void onClick()
    }}
  >
    {label}
  </button>
)

const EmptyActionCell = () => <div style={{ minHeight: "24px" }} />

function getBmdStatusMeta(
  activityStatus: number | undefined,
  flowStatus: string | null | undefined,
): { label: string; className: string } {
  const raw = (flowStatus || "").toUpperCase()
  if (activityStatus === 1 || raw === "COMPLETED") {
    return { label: "Completed", className: "text-success fw-700" }
  }
  if (raw === "IN_PROGRESS") {
    return { label: "In Progress", className: "text-warning fw-700" }
  }
  if (raw === "PAUSED") {
    return { label: "Paused", className: "text-muted fw-700" }
  }
  return { label: "Not Started", className: "cs-txt-danger fw-700" }
}

function getRolledOverText(rollDate: unknown): string | null {
  if (!rollDate || typeof rollDate !== "string") return null
  return `Rolled over from ${moment(rollDate, moment.ISO_8601).format(
    "DD/MMM/YYYY",
  )}`
}

export const BmdActivityCard: React.FC<BmdActivityCardProps> = ({
  activity,
  index,
  isHomework = false,
  isPlanningSession = false,
  checkbox,
  showContextMenu = true,
  onPreview,
  onDelete,
  onRollOver,
  onRolloverToNextLesson,
  onRolloverToHomework,
  onCopyToHomework,
  onCopyToLessonPlan,
  onMoveToHomework,
  onMoveToLessonPlan,
  deleteEnabled = false,
  rollOverEnabled = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()

  const isEnglish = activity.activity_type === ActivityType.ENGLISH_DIAGNOSTICS
  const session = isEnglish
    ? activity.eng_diag_session || null
    : activity.bmd_session || null
  const topicCount = activity.topic_count ?? 0
  const questionCount = activity.question_count ?? 0
  // The per-question pace is a Math figure; English questions carry their own
  // time allowance, so no estimate is shown for them.
  const estimatedMinutes = isEnglish
    ? null
    : Math.ceil((questionCount * BMD_SECONDS_PER_QUESTION) / 60)

  const statusMeta = getBmdStatusMeta(
    activity.status,
    session?.flow_status ?? null,
  )
  const rolledOverText = getRolledOverText(activity.roll_date)
  const levelPrefix = activity.level_name ? `${activity.level_name} — ` : ""
  const isPrereqTest = !!activity.is_prereq_test

  // Preview is deliberately NOT routed through the lesson-plan controller.
  // onPreviewActivity switches on activity_type and has no case for 13, so it
  // fell through to "Unknown activity type" - which is what the PM reported.
  // It could not have had one either: it redirects to a student-app preview
  // page, and a diagnostics flow has none. The card opens the modal itself, so
  // the controller never has to learn about diagnostics.
  const [previewOpen, setPreviewOpen] = useState(false)
  // The year level stays on the tutor's card — it is student-facing surfaces
  // that must never show it — and here it usefully says which diagnostic the
  // prerequisite test was built from.
  // Subject first. Both subjects use this card and both sit in the same lesson plan, so
  // "Year 2 — Diagnostics" alone does not say which test it is - and the year levels come from
  // different id spaces, so even the numbers do not disambiguate them. Derived from
  // activity_type rather than carried on the row: the type IS the subject here (13 is Baseline
  // MATH Diagnostics, 15 is English), so there is nothing to keep in step.
  const subjectPrefix = isEnglish ? "English — " : "Mathematics — "
  const titleText = `${subjectPrefix}${levelPrefix}${
    isPrereqTest ? "Prerequisite Test" : "Diagnostics"
  }`
  const markedForHomework = Number(activity.marked_for_homework) === 1

  // Move/Copy decisions (planning mode only). Labels deliberately match
  // getPlanningMoveAction / getPlanningCopyAction in PlanLessonCards.tsx —
  // the CEO flagged the diagnostics card reading "Move to homework" while
  // every other card said "Move to h/w" (2026-08-06). If those change, change
  // these with them.
  const moveAction = useMemo(() => {
    if (!isPlanningSession) return null
    if (!markedForHomework && onMoveToHomework) {
      return { label: "Move to h/w", onClick: onMoveToHomework }
    }
    if (markedForHomework && onMoveToLessonPlan) {
      return { label: "Move to lesson", onClick: onMoveToLessonPlan }
    }
    return null
  }, [isPlanningSession, markedForHomework, onMoveToHomework, onMoveToLessonPlan])

  const copyAction = useMemo(() => {
    if (isPlanningSession) {
      if (!markedForHomework && onCopyToHomework) {
        return { label: "Copy to h/w", onClick: onCopyToHomework }
      }
      if (markedForHomework && onCopyToLessonPlan) {
        return { label: "Copy to lesson", onClick: onCopyToLessonPlan }
      }
      return null
    }
    // Session (not planning) — only show Copy to homework
    if (!isHomework && !markedForHomework && onCopyToHomework) {
      return { label: "Copy to h/w", onClick: onCopyToHomework }
    }
    return null
  }, [
    isPlanningSession,
    isHomework,
    markedForHomework,
    onCopyToHomework,
    onCopyToLessonPlan,
  ])

  // Menu contains overflow items — rollover, anything not in the primary buttons.
  const menuItems = useMemo(() => {
    const items: Array<{
      key: string
      label: string
      onClick: () => unknown
    }> = []
    if (rollOverEnabled && onRollOver) {
      items.push({
        key: "rollOver",
        label: "Roll over to next session",
        onClick: onRollOver,
      })
    }
    return items
  }, [rollOverEnabled, onRollOver])

  return (
    <>
    <div
      className="set1 bg-supermild"
      data-key={activity.pk_lesson_activity_key}
      style={{ minHeight: "unset", padding: "4px 6px" }}
    >
      <div className="d-flex justify-content-between align-items-start gap-1">
        {/* Left column — content */}
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {rolledOverText ? (
            <span
              className="cs-txt-danger me-2"
              style={{ fontSize: "8px", display: "block", lineHeight: 1.1 }}
            >
              {rolledOverText}
            </span>
          ) : null}

          <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
            <span
              className={statusMeta.className}
              style={{ fontSize: "8px", lineHeight: 1 }}
            >
              {statusMeta.label}
            </span>

            {/* A prerequisite test GROWS while the student takes it — a wrong
                answer earns the next question down the chain — so this reads
                as a running total, not a final length. The counts themselves
                are recomputed server-side on every fetch, so re-opening the
                page shows where the test has got to.

                No time estimate for a prerequisite test: the length is not
                knowable up front, and the time budget was settled as covering
                the base test only. */}
            <span
              className="text-dark"
              style={{ fontSize: "8px", lineHeight: 1 }}
              title={
                isPrereqTest
                  ? `${topicCount} section${
                      topicCount === 1 ? "" : "s"
                    }, ${questionCount} question${
                      questionCount === 1 ? "" : "s"
                    } so far. More are added as the student answers.`
                  : `${topicCount} topic${
                      topicCount === 1 ? "" : "s"
                    }, ${questionCount} question${
                      questionCount === 1 ? "" : "s"
                    }${
                      estimatedMinutes == null
                        ? ""
                        : `, about ${estimatedMinutes} minutes`
                    }`
              }
            >
              {isPrereqTest ? (
                <>
                  {topicCount}t · {questionCount}q so far
                </>
              ) : (
                <>
                  {topicCount} topic{topicCount === 1 ? "" : "s"} ·{" "}
                  {questionCount} question{questionCount === 1 ? "" : "s"}
                  {estimatedMinutes == null ? null : <> · ~{estimatedMinutes} min</>}
                </>
              )}
            </span>

            {activity?.tutor_comments ? (
              <span
                className="text-dark"
                style={{ cursor: "pointer", fontSize: "9px", lineHeight: 1 }}
                title={`Comment: ${activity.tutor_comments}`}
              >
                <i className="fas fa-info-circle"></i>
              </span>
            ) : null}

            {checkbox?.checkable ? (
              <input
                type="checkbox"
                checked={!!checkbox.checked}
                onMouseDown={(event) => event.stopPropagation()}
                onChange={(event) => {
                  event.stopPropagation()
                  void checkbox.onCheckedChanged?.()
                }}
              />
            ) : null}
          </div>

          <h6
            className="text-dark fw-700"
            style={{
              fontSize: "10px",
              lineHeight: 1.15,
              marginBottom: "3px",
              overflow: "hidden",
              wordBreak: "break-word",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
            title={titleText}
          >
            {index + 1}) {titleText}
          </h6>

          <div
            className="border-top"
            style={{ marginTop: "2px", marginBottom: "3px", opacity: 0.7 }}
          ></div>

          <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "9px", lineHeight: 1.05 }}
            >
              <span>
                <img className="me-05" width={12} src={reel} alt="" />
              </span>
              {isPrereqTest ? "Prerequisite Test" : "Diagnostics"}
            </div>
          </div>

          <div className="question-count mt-0">
            <div style={{ fontSize: "9px" }}>&nbsp;</div>
          </div>
        </div>

        {/* Right column — action buttons + menu */}
        {showContextMenu ? (
          <div
            style={{
              width: `${COMPACT_CARD_ACTION_WIDTH}px`,
              flex: `0 0 ${COMPACT_CARD_ACTION_WIDTH}px`,
            }}
          >
            <div className="d-grid" style={{ gap: "3px" }}>
              {/* Only English can have answers a tutor still has to mark. Until they are
                  marked the test counts for nothing, so the action sits above Preview. */}
              {isEnglish && (activity.pending_marking_count ?? 0) > 0 ? (
                <CompactActionButton
                  label={`Mark answers (${activity.pending_marking_count})`}
                  onClick={() =>
                    navigate(`/eng-diag-marking/${activity.pk_lesson_activity_key}`)
                  }
                  tone="secondary"
                />
              ) : null}

              <CompactActionButton
                label="Preview"
                onClick={() => {
                  // A caller may still pass onPreview for its own bookkeeping;
                  // the modal is what actually shows the test.
                  if (onPreview) onPreview()
                  setPreviewOpen(true)
                }}
                tone="preview"
              />

              {!isPlanningSession &&
              rollOverEnabled &&
              (onRolloverToNextLesson || onRolloverToHomework) ? (
                <div
                  className="d-grid"
                  style={{ gridTemplateColumns: "1fr 1fr", gap: "3px" }}
                >
                  {onRolloverToNextLesson ? (
                    <CompactActionButton
                      label={
                        <>
                          <i
                            className="mdi mdi-redo"
                            style={{ fontSize: "10px", marginRight: "2px" }}
                            aria-hidden="true"
                          />
                          To lesson
                        </>
                      }
                      onClick={onRolloverToNextLesson}
                      tone="secondary"
                      title="Rollover to next Lesson"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}
                  {onRolloverToHomework ? (
                    <CompactActionButton
                      label={
                        <>
                          <i
                            className="mdi mdi-redo"
                            style={{ fontSize: "10px", marginRight: "2px" }}
                            aria-hidden="true"
                          />
                          To h/w
                        </>
                      }
                      onClick={onRolloverToHomework}
                      tone="secondary"
                      title="Rollover to Homework"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}
                </div>
              ) : null}

              <div
                className="d-grid"
                style={{ gridTemplateColumns: "1fr 1fr", gap: "3px" }}
              >
                <div className="d-grid" style={{ gap: "3px" }}>
                  {deleteEnabled && onDelete ? (
                    <CompactActionButton
                      label="Delete"
                      onClick={onDelete}
                      tone="delete"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}

                  {menuItems.length > 0 ? (
                    <Dropdown
                      isOpen={menuOpen}
                      toggle={() => setMenuOpen((prev) => !prev)}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <DropdownToggle
                        caret={false}
                        className="btn btn-sm w-100 fw-700"
                        style={{
                          ...getActionButtonStyle("menu"),
                          fontSize: "9px",
                        }}
                      >
                        <i className="mdi mdi-dots-horizontal"></i>
                      </DropdownToggle>
                      <DropdownMenu className="custom-position">
                        {menuItems.map((item) => (
                          <DropdownItem
                            key={item.key}
                            onClick={(event) => {
                              event.stopPropagation()
                              void item.onClick()
                            }}
                          >
                            {item.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  ) : (
                    <EmptyActionCell />
                  )}
                </div>

                <div className="d-grid" style={{ gap: "3px" }}>
                  {moveAction ? (
                    <CompactActionButton
                      label={moveAction.label}
                      onClick={moveAction.onClick}
                      tone="secondary"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}

                  {copyAction ? (
                    <CompactActionButton
                      label={copyAction.label}
                      onClick={copyAction.onClick}
                      tone="secondary"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>

    {/* Previewed from the ASSIGNED session's own questions, not from a year
        and topic list: a prerequisite test is built from skills and has
        neither, and even a base test's content may have changed since. */}
    {previewOpen && isEnglish ? (
      <EngDiagPreviewModal
        isOpen
        onClose={() => setPreviewOpen(false)}
        yearLevelLabel={activity.level_name || ""}
        activityKey={activity.pk_lesson_activity_key}
      />
    ) : null}
    {previewOpen && !isEnglish ? (
      <BmdPreviewModal
        isOpen
        onClose={() => setPreviewOpen(false)}
        yearLevelLabel={activity.level_name || ""}
        activityKey={activity.pk_lesson_activity_key}
        studentKey={activity.fk_student_key}
      />
    ) : null}
    </>
  )
}

export default BmdActivityCard
